/**
 * PeripheralImportDialog — importar «Otros periféricos» desde Excel o CSV.
 *
 * Tres pasos en un solo diálogo:
 *  1. **Fichero**: se elige o se arrastra un .xlsx/.xls/.csv. La lectura se hace
 *     en el navegador con SheetJS (ya está en el bundle para exportar), así que
 *     el fichero NO se sube: solo viajan las filas ya mapeadas.
 *  2. **Mapeo**: se detectan las cabeceras («Código», «Nombre», «IP»,
 *     «Ubicación»…) y se pueden corregir con un desplegable por campo. Debajo
 *     hay una vista previa de lo que se va a importar.
 *  3. **Resultado**: creados / actualizados / omitidos, con errores y avisos
 *     por fila del backend.
 *
 * Duplicados: manda el código. Si ya existe, se ACTUALIZA (opción marcada por
 * defecto) o se omite. Las ubicaciones se casan por código, nombre o ruta
 * completa; los tipos que no existan se crean.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import Dialog from './common/Dialog';
import api from '../services/api';

/* ── Campos del modelo que se pueden importar ──────────────────────────── */

const CAMPOS = [
  {
    key: 'code', label: 'Código', clave: true,
    aliases: ['codigo', 'code', 'codi', 'cod', 'referencia', 'ref', 'id'],
  },
  {
    key: 'name', label: 'Nombre', clave: true,
    aliases: ['nombre', 'name', 'nom', 'dispositivo', 'descripcion', 'descripcio', 'equipo', 'elemento'],
  },
  {
    key: 'peripheral_type', label: 'Tipo',
    aliases: ['tipo', 'type', 'tipus', 'categoria', 'categoria', 'clase'],
  },
  {
    key: 'location', label: 'Ubicación',
    aliases: ['ubicacion', 'location', 'ubicacio', 'sede', 'site', 'emplazamiento', 'emplacament', 'localizacion', 'localitzacio'],
  },
  {
    key: 'manufacturer', label: 'Fabricante',
    aliases: ['fabricante', 'manufacturer', 'marca', 'fabricant', 'maker'],
  },
  { key: 'model', label: 'Modelo', aliases: ['modelo', 'model'] },
  {
    key: 'ip_address', label: 'IP',
    aliases: ['ip', 'direccion ip', 'ip address', 'adreca ip', 'adreca', 'ip gestion'],
  },
  {
    key: 'mac_address', label: 'MAC',
    aliases: ['mac', 'mac address', 'direccion mac', 'adreca mac'],
  },
  {
    key: 'management_url', label: 'URL de gestión',
    aliases: ['url', 'url gestion', 'gestion', 'management url', 'enlace', 'link', 'web'],
  },
  {
    key: 'has_wifi', label: 'WiFi (sí/no)',
    aliases: ['wifi', 'tiene wifi', 'te wifi', 'con wifi', 'has wifi'],
  },
  {
    key: 'wifi_ssid', label: 'SSID WiFi',
    aliases: ['ssid', 'wifi ssid', 'red wifi', 'xarxa wifi'],
  },
  {
    key: 'room', label: 'Sala',
    aliases: ['sala', 'room', 'despacho', 'habitacion', 'habitacio', 'oficina'],
  },
  {
    key: 'floor', label: 'Planta',
    aliases: ['planta', 'floor', 'piso'],
  },
  {
    key: 'serial_number', label: 'Nº de serie',
    aliases: ['serial', 'numero de serie', 'n serie', 'nº serie', 'sn', 'serialnumber', 'serial number', 'numero serie'],
  },
  {
    key: 'purchase_date', label: 'Fecha de compra',
    aliases: ['fecha compra', 'fecha de compra', 'purchase date', 'data compra', 'fecha adquisicion'],
  },
  {
    key: 'status', label: 'Estado',
    aliases: ['estado', 'status', 'estat', 'situacion'],
  },
  {
    key: 'notes', label: 'Notas',
    aliases: ['notas', 'notes', 'observaciones', 'comentarios', 'comentaris'],
  },
];

/*: Cabeceras de la plantilla descargable + fila de ejemplo. */
const PLANTILLA = {
  cabeceras: [
    'Código', 'Nombre', 'Tipo', 'Ubicación', 'Fabricante', 'Modelo', 'IP', 'MAC',
    'URL de gestión', 'WiFi (sí/no)', 'SSID WiFi', 'Sala', 'Planta', 'Nº de serie',
    'Fecha de compra', 'Estado', 'Notas',
  ],
  ejemplo: [
    'PERIPH-001', 'Televisión sala de reuniones', 'Televisión', 'Sede › Sala 3',
    'Samsung', 'QE55', '192.168.1.50', '', '', 'Sí', 'CORP-WIFI', 'Sala 3',
    'Planta 1', 'SN12345', '2026-01-15', 'Activo', 'Ejemplo: se puede borrar',
  ],
};

const norm = (value) => String(value ?? '')
  .replace(/\ufeff/g, '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

/** Texto de una celda, con las fechas de Excel en ISO (YYYY-MM-DD). */
function textoCelda(value) {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return String(value).trim();
}

/** Cabeza de la tabla: la primera fila con al menos dos celdas con texto. */
function buscarCabecera(matrix) {
  for (let i = 0; i < Math.min(matrix.length, 15); i += 1) {
    const celdas = (matrix[i] || []).filter((c) => textoCelda(c) !== '');
    if (celdas.length >= 2) return i;
  }
  return 0;
}

/** Mapeo automático por parecido de cabeceras (sin repetir columna). */
function detectarMapeo(headers) {
  const usadas = new Set();
  const pool = headers.map((header, index) => ({ header: norm(header), index }));
  const mapeo = {};
  CAMPOS.forEach((campo) => {
    let encontrada = null;
    // 1) Coincidencia exacta con un alias.
    for (const item of pool) {
      if (usadas.has(item.index) || !item.header) continue;
      if (campo.aliases.includes(item.header)) { encontrada = item.index; break; }
    }
    // 2) Coincidencia parcial («Dirección IP» contiene «ip»).
    if (encontrada === null) {
      for (const item of pool) {
        if (usadas.has(item.index) || !item.header) continue;
        if (campo.aliases.some((alias) => alias.length >= 3
          && (item.header.includes(alias) || alias.includes(item.header)))) {
          encontrada = item.index;
          break;
        }
      }
    }
    if (encontrada !== null) {
      mapeo[campo.key] = String(encontrada);
      usadas.add(encontrada);
    }
  });
  return mapeo;
}

export default function PeripheralImportDialog({ companyId, onClose }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);

  const fileRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [sheetName, setSheetName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState('');
  const [busyFile, setBusyFile] = useState(false);
  const [importing, setImporting] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [createTypes, setCreateTypes] = useState(true);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);

  /* ── Leer el fichero (local, sin subirlo) ───────────────────────────── */
  const leerFichero = async (file) => {
    if (!file) return;
    setError('');
    setResult(null);
    setBusyFile(true);
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      let wb;
      if (/\.csv$/i.test(file.name)) {
        // El CSV se decodifica como UTF-8 a mano: si se deja a SheetJS, un
        // fichero UTF-8 sin BOM se lee como ANSI y salen «CÃ³digo» y «TelevisiÃ³n».
        const texto = new TextDecoder('utf-8').decode(buffer);
        // CSV español: a veces va con «;». Se elige el separador más repetido
        // en la primera línea para no partir mal las columnas.
        const primera = (texto.split(/\r?\n/)[0] || '');
        const cuenta = {
          ',': (primera.match(/,/g) || []).length,
          ';': (primera.match(/;/g) || []).length,
          '\t': (primera.match(/\t/g) || []).length,
        };
        const fs = Object.entries(cuenta).sort((a, b) => b[1] - a[1])[0][0];
        wb = XLSX.read(texto, { type: 'string', FS: fs, cellDates: true, codepage: 65001 });
      } else {
        wb = XLSX.read(buffer, { type: 'array', cellDates: true });
      }
      const hoja = wb.SheetNames[0];
      const sheet = wb.Sheets[hoja];
      const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '' });
      const cabeceraIndex = buscarCabecera(matrix);
      const cabeceras = (matrix[cabeceraIndex] || []).map((c) => textoCelda(c));
      const datos = matrix
        .slice(cabeceraIndex + 1)
        .map((cells, offset) => ({
          cells: cells || [],
          excelRow: cabeceraIndex + offset + 2, // 1-based, contando la cabecera
        }))
        .filter(({ cells }) => cells.some((c) => textoCelda(c) !== ''));

      if (!cabeceras.some(Boolean) || !datos.length) {
        throw new Error(tr('peripheral.import_no_rows', 'El fichero no tiene datos'));
      }

      setFileName(file.name);
      setSheetName(hoja);
      setHeaders(cabeceras);
      setRows(datos);
      setMapping(detectarMapeo(cabeceras));
    } catch (err) {
      setError(err.message || tr('peripheral.import_no_file', 'No se ha podido leer el fichero'));
      setFileName('');
      setHeaders([]);
      setRows([]);
      setMapping({});
    } finally {
      setBusyFile(false);
    }
  };

  const onPickFile = (event) => {
    const file = event.target.files?.[0];
    if (file) leerFichero(file);
    event.target.value = '';
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) leerFichero(file);
  };

  /* ── Filas ya mapeadas ──────────────────────────────────────────────── */
  const filasMapeadas = useMemo(() => rows.map(({ cells, excelRow }) => {
    const fila = { __row: excelRow };
    CAMPOS.forEach((campo) => {
      const indice = mapping[campo.key];
      if (indice === undefined || indice === '') return;
      const valor = textoCelda(cells[Number(indice)]);
      if (valor !== '') fila[campo.key] = valor;
    });
    return fila;
  }), [rows, mapping]);

  const filasValidas = useMemo(
    () => filasMapeadas.filter((fila) => fila.code || fila.name),
    [filasMapeadas],
  );
  const filasSinClave = filasMapeadas.length - filasValidas.length;
  const tieneClave = Boolean(mapping.code !== undefined && mapping.code !== '')
    || Boolean(mapping.name !== undefined && mapping.name !== '');

  const preview = filasValidas.slice(0, 8);

  /* ── Importar ───────────────────────────────────────────────────────── */
  const importar = async () => {
    if (!filasValidas.length || !companyId) return;
    setImporting(true);
    setError('');
    try {
      const payload = {
        rows: filasValidas.map(({ __row, ...fila }) => fila),
        update_existing: updateExisting,
        create_types: createTypes,
      };
      const res = await api.importPeripherals(companyId, payload);
      setResult(res.data || null);
    } catch (err) {
      setError(err.message || tr('peripheral.import_error', 'No se pudo importar'));
    } finally {
      setImporting(false);
    }
  };

  /* ── Plantilla ──────────────────────────────────────────────────────── */
  const descargarPlantilla = async () => {
    try {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.aoa_to_sheet([PLANTILLA.cabeceras, PLANTILLA.ejemplo]);
      ws['!cols'] = PLANTILLA.cabeceras.map((cabecera) => ({ wch: Math.max(14, cabecera.length + 2) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Periféricos');
      XLSX.writeFile(wb, 'plantilla-perifericos.xlsx');
    } catch (err) {
      setError(err.message || 'No se pudo generar la plantilla');
    }
  };

  const etiquetaCampo = (campo) => tr(`peripheral.import_field_${campo.key}`, campo.label);

  const footer = result ? (
    <button type="button" className="list-btn-primary" onClick={() => onClose?.()}>
      {tr('peripheral.import_close', 'Cerrar')}
    </button>
  ) : (
    <>
      <button type="button" className="list-btn-secondary" onClick={() => onClose?.()} disabled={importing}>
        {tr('action.cancel', 'Cancelar')}
      </button>
      <button type="button" className="list-btn-secondary" onClick={descargarPlantilla} disabled={importing}>
        <ArrowDownTrayIcon style={{ width: 15, height: 15, verticalAlign: '-2px', marginRight: 4 }} />
        {tr('peripheral.import_template', 'Descargar plantilla')}
      </button>
      <button
        type="button"
        className="list-btn-primary"
        onClick={importar}
        disabled={!filasValidas.length || importing}
        title={!tieneClave
          ? tr('peripheral.import_need_key', 'Mapea al menos Código o Nombre')
          : undefined}
      >
        {importing
          ? tr('peripheral.import_running', 'Importando…')
          : `${tr('peripheral.import_run', 'Importar')} ${filasValidas.length}`}
      </button>
    </>
  );

  return (
    <Dialog
      title={tr('peripheral.import_title', 'Importar periféricos')}
      icon="📥"
      maxWidth={860}
      busy={importing}
      onClose={() => { if (!importing) onClose?.(); }}
      footer={footer}
    >
      <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
        {!result && (
          <>
            <p className="list-hint" style={{ marginTop: 0 }}>
              {tr(
                'peripheral.import_hint',
                'Sube un Excel (.xlsx/.xls) o un CSV. El fichero se lee en tu navegador: después ajustas qué columna va a cada campo y revisas la vista previa antes de importar.',
              )}
            </p>

            {/* Zona de fichero */}
            <div
              onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              style={{
                border: `2px dashed ${dragging ? 'var(--primary-500, #2563eb)' : 'var(--border-color, #cbd5e1)'}`,
                borderRadius: 10,
                padding: '18px 16px',
                textAlign: 'center',
                marginBottom: 14,
                background: dragging ? 'var(--bg-hover, rgba(37,99,235,0.06))' : 'transparent',
                cursor: 'pointer',
              }}
              onClick={() => fileRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => { if (event.key === 'Enter') fileRef.current?.click(); }}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={onPickFile}
              />
              <ArrowUpTrayIcon style={{ width: 26, height: 26, opacity: 0.7 }} />
              <div style={{ marginTop: 6, fontWeight: 600 }}>
                {busyFile
                  ? tr('msg.loading', 'Cargando…')
                  : (fileName || tr('peripheral.import_drop', 'Arrastra aquí el fichero o pulsa para elegirlo'))}
              </div>
              {fileName && (
                <div className="list-hint" style={{ marginTop: 4 }}>
                  {sheetName} · {rows.length} {tr('peripheral.import_rows', 'filas detectadas')}
                  {' · '}
                  <span style={{ textDecoration: 'underline' }}>
                    {tr('peripheral.import_change', 'cambiar fichero')}
                  </span>
                </div>
              )}
            </div>

            {error && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: 'rgba(220, 38, 38, 0.10)',
                  color: 'var(--red-600, #dc2626)',
                  fontSize: '0.84rem',
                }}
              >
                {error}
              </div>
            )}

            {headers.length > 0 && (
              <>
                {/* Mapeo */}
                <h5 style={{ margin: '4px 0 8px' }}>
                  {tr('peripheral.import_mapping', 'Columnas del fichero')}
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px 14px', marginBottom: 14 }}>
                  {CAMPOS.map((campo) => (
                    <label key={campo.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem' }}>
                      <span style={{ minWidth: 110, opacity: 0.85 }}>
                        {etiquetaCampo(campo)}
                        {campo.clave && <span style={{ color: 'var(--red-600, #dc2626)' }}> *</span>}
                      </span>
                      <select
                        className="list-input"
                        style={{ flex: 1, minWidth: 0 }}
                        value={mapping[campo.key] ?? ''}
                        onChange={(event) => setMapping((prev) => ({ ...prev, [campo.key]: event.target.value }))}
                      >
                        <option value="">{tr('peripheral.import_ignore', '— No importar —')}</option>
                        {headers.map((header, index) => (
                          <option key={`${campo.key}-${index}`} value={String(index)}>
                            {header || `${tr('peripheral.import_column', 'Columna')} ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>

                {/* Opciones */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 22px', marginBottom: 14 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '0.84rem' }}>
                    <input
                      type="checkbox"
                      checked={updateExisting}
                      onChange={(event) => setUpdateExisting(event.target.checked)}
                    />
                    {tr('peripheral.import_update', 'Actualizar los que ya existen (mismo código)')}
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '0.84rem' }}>
                    <input
                      type="checkbox"
                      checked={createTypes}
                      onChange={(event) => setCreateTypes(event.target.checked)}
                    />
                    {tr('peripheral.import_create_types', 'Crear los tipos que falten')}
                  </label>
                </div>

                {/* Vista previa */}
                <h5 style={{ margin: '4px 0 8px' }}>
                  {tr('peripheral.import_preview', 'Vista previa')}
                  {' '}
                  <span className="list-hint" style={{ fontWeight: 400 }}>
                    {filasValidas.length} {tr('peripheral.import_ready', 'filas listas')}
                    {filasSinClave > 0 && (
                      <> · {filasSinClave} {tr('peripheral.import_no_key', 'sin código ni nombre (se omiten)')}</>
                    )}
                  </span>
                </h5>
                <div style={{ overflowX: 'auto', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 8 }}>
                  <table className="list-table" style={{ width: '100%', fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <th>{tr('peripheral.import_row', 'Fila')}</th>
                        <th>{tr('peripheral.code', 'Código')}</th>
                        <th>{tr('list.name', 'Nombre')}</th>
                        <th>{tr('peripheral.type', 'Tipo')}</th>
                        <th>{tr('list.location', 'Ubicación')}</th>
                        <th>IP</th>
                        <th>{tr('list.status', 'Estado')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((fila) => (
                        <tr key={fila.__row}>
                          <td className="list-hint">{fila.__row}</td>
                          <td>{fila.code || <span className="list-hint">—</span>}</td>
                          <td>{fila.name || <span className="list-hint">—</span>}</td>
                          <td>{fila.peripheral_type || <span className="list-hint">—</span>}</td>
                          <td>{fila.location || <span className="list-hint">—</span>}</td>
                          <td>{fila.ip_address || <span className="list-hint">—</span>}</td>
                          <td>{fila.status || <span className="list-hint">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filasValidas.length > preview.length && (
                  <p className="list-hint" style={{ marginTop: 6 }}>
                    {tr('peripheral.import_more', '… y')} {filasValidas.length - preview.length}{' '}
                    {tr('peripheral.import_more_rows', 'filas más')}
                  </p>
                )}
                {!tieneClave && (
                  <p style={{ marginTop: 8, color: 'var(--red-600, #dc2626)', fontSize: '0.84rem' }}>
                    {tr('peripheral.import_need_key', 'Mapea al menos Código o Nombre')}
                  </p>
                )}
              </>
            )}
          </>
        )}

        {/* Resultado */}
        {result && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              {[
                ['peripheral.import_created', 'Creados', result.created || 0],
                ['peripheral.import_updated', 'Actualizados', result.updated || 0],
                ['peripheral.import_skipped', 'Omitidos', result.skipped || 0],
              ].map(([clave, reserva, total]) => (
                <div
                  key={clave}
                  style={{
                    minWidth: 110,
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: '1px solid var(--border-color, #e2e8f0)',
                  }}
                >
                  <div className="list-hint" style={{ fontSize: '0.78rem' }}>{tr(clave, reserva)}</div>
                  <b style={{ fontSize: '1.25rem' }}>{total}</b>
                </div>
              ))}
            </div>

            {(result.errors || []).length > 0 && (
              <>
                <h5 style={{ margin: '0 0 6px', color: 'var(--red-600, #dc2626)' }}>
                  {tr('peripheral.import_errors', 'Errores')} ({result.errors.length})
                </h5>
                <ul className="list-hint" style={{ margin: '0 0 12px 18px', padding: 0 }}>
                  {result.errors.slice(0, 50).map((item, index) => (
                    <li key={`e-${index}`}>
                      {tr('peripheral.import_row', 'Fila')} {item.row}
                      {item.code ? ` · ${item.code}` : ''}: {item.error}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {(result.warnings || []).length > 0 && (
              <>
                <h5 style={{ margin: '0 0 6px' }}>
                  {tr('peripheral.import_warnings', 'Avisos')} ({result.warnings.length})
                </h5>
                <ul className="list-hint" style={{ margin: '0 0 12px 18px', padding: 0 }}>
                  {result.warnings.slice(0, 50).map((item, index) => (
                    <li key={`w-${index}`}>
                      {tr('peripheral.import_row', 'Fila')} {item.row}
                      {item.code ? ` · ${item.code}` : ''}: {item.warning}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {!result.errors?.length && !result.warnings?.length && (
              <p className="list-hint">
                {tr('peripheral.import_all_ok', 'Todas las filas se han importado sin incidencias.')}
              </p>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
